import React from 'react';

import { useStoreContext } from 'features/store';

import { useFeatureMenu } from 'hooks/use-feature-menu';
import { useLocale } from 'state/intl';
import { useMainMenuContext } from 'state/menu/main-menu';

/**
 * Exposes a method to fetch the MainMenu, reading all deps from global contexts
 *
 * @returns a fetching function
 */
export const useFetchMainMenu = () => {
  const { getMainMenu } = useMainMenuContext();
  const { menuId } = useFeatureMenu();
  const { region } = useLocale();
  const { store } = useStoreContext();
  const storeId = store.number ?? '';

  return React.useCallback(
    (forceRefresh: boolean) => getMainMenu({ menuId, region, storeId, forceRefresh }),
    [getMainMenu, menuId, region, storeId]
  );
};

/**
 * Fetches the MainMenu once.
 * It keeps fetching until all deps are settled.
 * When the first fetch was successful, it stops fetching.
 *
 * @returns a boolean that indicates if it already performed the fetching.
 */
export const useFetchMainMenuOnMount = (): boolean => {
  const [isPending, setIsPending] = React.useState(true);
  const fetchMainMenu = useFetchMainMenu();

  // Executes the menu fetch every time the `fetchMainMenu` deps changes
  // When the first fetch was successful, we stop fetching
  React.useEffect(() => {
    const hasExecutedFetch = fetchMainMenu(false);
    if (hasExecutedFetch) {
      setIsPending(false);
    }
  }, [fetchMainMenu]);

  // This flag does not mean that all the data is settled.
  // It means we triggered the fetching.
  return isPending;
};
